import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";

const BUSINESS_LINKS = [
  { to: "/businesses/mangalam-enterprises", label: "Mangalam Enterprises", sub: "Industrial & CVR Tyres" },
  { to: "/businesses/apex-infra", label: "Apex Infra", sub: "Earthmoving & Off-Highway" },
  { to: "/businesses/amr", label: "Apex Mineral Resources", sub: "Tyre Management & Mining" },
];

const SOLUTION_LINKS = [
  { to: "/products?application=Mining", label: "Mining" },
  { to: "/products?application=Earthmoving", label: "Earthmoving" },
  { to: "/products?application=Industrial", label: "Industrial" },
  { to: "/products?application=Commercial Vehicles", label: "Commercial Vehicles" },
  { to: "/businesses/amr", label: "Tyre Management" },
];

const linkCls = ({ isActive }) =>
  `font-mono text-[11px] tracking-[0.2em] transition-colors py-2 ${
    isActive ? "text-ember" : "text-slate-300 hover:text-white"
  }`;

function Dropdown({ label, testid, children }) {
  return (
    <div className="relative group">
      <button
        data-testid={testid}
        className="flex items-center gap-1.5 font-mono text-[11px] tracking-[0.2em] text-slate-300 hover:text-white transition-colors py-2"
      >
        {label}
        <ChevronDown className="h-3 w-3 opacity-60 transition-transform duration-200 group-hover:rotate-180" />
      </button>
      <div className="absolute left-0 top-full pt-3 opacity-0 translate-y-1 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition duration-200">
        <div className="w-72 border border-white/10 bg-obsidian-2/95 backdrop-blur-md shadow-2xl shadow-black/60">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed inset-x-0 top-0 z-50 bg-obsidian-2/90 backdrop-blur-md border-b border-white/10 transition-colors duration-300"
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="nav-logo" aria-label="Mangalam Group home">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            <NavLink to="/" end className={linkCls} data-testid="nav-home">HOME</NavLink>
            <NavLink to="/about" className={linkCls} data-testid="nav-about">ABOUT</NavLink>
            <Dropdown label="OUR BUSINESSES" testid="nav-businesses">
              {BUSINESS_LINKS.map((b) => (
                <Link
                  key={b.to}
                  to={b.to}
                  data-testid={`nav-business-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="block border-b border-white/5 px-5 py-4 last:border-0 hover:bg-white/5 transition-colors"
                >
                  <span className="block font-condensed text-lg font-semibold uppercase tracking-wide text-white">
                    {b.label}
                  </span>
                  <span className="block font-mono text-[10px] tracking-[0.18em] text-slate-400 mt-0.5">
                    {b.sub}
                  </span>
                </Link>
              ))}
            </Dropdown>
            <Dropdown label="SOLUTIONS" testid="nav-solutions">
              {SOLUTION_LINKS.map((s) => (
                <Link
                  key={s.label}
                  to={s.to}
                  data-testid={`nav-solution-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex items-center justify-between border-b border-white/5 px-5 py-3.5 last:border-0 font-condensed text-lg font-semibold uppercase tracking-wide text-slate-200 hover:bg-white/5 hover:text-ember transition-colors"
                >
                  {s.label}
                  <ArrowUpRight className="h-4 w-4 opacity-50" />
                </Link>
              ))}
            </Dropdown>
            <NavLink to="/products" className={linkCls} data-testid="nav-products">PRODUCTS</NavLink>
            <NavLink to="/bkt" className={linkCls} data-testid="nav-bkt">BKT</NavLink>
            <NavLink to="/insights" className={linkCls} data-testid="nav-insights">INSIGHTS</NavLink>
            <NavLink to="/contact" className={linkCls} data-testid="nav-contact">CONTACT</NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-cta"
              className="hidden sm:inline-flex items-center gap-2 bg-ember px-5 py-2.5 font-mono text-[11px] tracking-[0.2em] text-[#10141B] transition-colors hover:bg-ember-hover"
            >
              GET IN TOUCH
            </Link>
            <button
              data-testid="nav-hamburger"
              onClick={() => setOpen(true)}
              className="lg:hidden text-white p-2"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div data-testid="mobile-menu" className="fixed inset-0 z-[60] bg-obsidian flex flex-col">
          <div className="flex h-[72px] items-center justify-between px-4 sm:px-6 border-b border-white/10">
            <Logo />
            <button
              data-testid="mobile-menu-close"
              onClick={() => setOpen(false)}
              className="text-white p-2"
              aria-label="Close menu"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-6 py-8 space-y-6" aria-label="Mobile">
            <Link to="/" data-testid="mobile-nav-home" className="block font-condensed text-3xl font-semibold uppercase text-white">Home</Link>
            <Link to="/about" data-testid="mobile-nav-about" className="block font-condensed text-3xl font-semibold uppercase text-white">About</Link>
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-ember mb-3">OUR BUSINESSES</p>
              {BUSINESS_LINKS.map((b) => (
                <Link key={b.to} to={b.to} data-testid={`mobile-nav-${b.label.toLowerCase().replace(/\s+/g, "-")}`} className="block py-1.5 font-condensed text-2xl font-medium uppercase text-slate-200">
                  {b.label}
                </Link>
              ))}
            </div>
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-ember mb-3">SOLUTIONS</p>
              {SOLUTION_LINKS.map((s) => (
                <Link key={s.label} to={s.to} data-testid={`mobile-nav-solution-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="block py-1.5 font-condensed text-2xl font-medium uppercase text-slate-200">
                  {s.label}
                </Link>
              ))}
            </div>
            <Link to="/products" data-testid="mobile-nav-products" className="block font-condensed text-3xl font-semibold uppercase text-white">Products</Link>
            <Link to="/bkt" data-testid="mobile-nav-bkt" className="block font-condensed text-3xl font-semibold uppercase text-white">BKT</Link>
            <Link to="/insights" data-testid="mobile-nav-insights" className="block font-condensed text-3xl font-semibold uppercase text-white">Insights</Link>
            <Link to="/contact" data-testid="mobile-nav-contact" className="block font-condensed text-3xl font-semibold uppercase text-white">Contact</Link>
          </nav>
          <div className="border-t border-white/10 p-6">
            <Link
              to="/contact"
              data-testid="mobile-nav-cta"
              className="flex items-center justify-center gap-2 bg-ember px-5 py-4 font-mono text-xs tracking-[0.25em] text-[#10141B]"
            >
              GET IN TOUCH <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
